import { useEffect, useRef, useState } from 'react';
import { Button, Input, type InputProps } from '@fluentui/react-components';
import { CalendarLtr20Regular } from '@fluentui/react-icons';
import { useLocaleSettings } from '../../hooks/useLocaleSettings';
import {
  dateFormatHint, isoToDisplayDate, parseDisplayDate, type DateFormat,
} from '../../utils/locale-format';

export interface DateInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'defaultValue' | 'contentAfter'> {
  /** Fecha ISO "yyyy-MM-dd" ('' = sin fecha). Es lo que se envía al backend. */
  value: string | undefined | null;
  /** Recibe "yyyy-MM-dd" cuando el texto es una fecha válida, o '' si se vacía / es inválido. */
  onValueChange: (iso: string) => void;
}

/**
 * Campo de fecha (#56) que sigue la configuración regional (`locale.dateFormat`,
 * por defecto dd/MM/yyyy): el placeholder y el texto mostrado usan ese formato en
 * lugar del `mm/dd/yyyy` del `<input type="date">` nativo (que depende del
 * navegador). El valor que viaja al formulario/backend sigue siendo ISO.
 * El botón de calendario abre el selector nativo como ayuda.
 */
export function DateInput({ value, onValueChange, disabled, ...rest }: DateInputProps) {
  const fmt: DateFormat = useLocaleSettings().dateFormat ?? 'dd/MM/yyyy';
  const iso = value ?? '';
  const [text, setText] = useState(() => isoToDisplayDate(iso, fmt));
  const [invalid, setInvalid] = useState(false);
  const nativeRef = useRef<HTMLInputElement | null>(null);
  const lastIso = useRef(iso);
  const lastFmt = useRef(fmt);

  // Cambio externo (reset, edición) o del formato regional → vuelve a mostrar el valor.
  useEffect(() => {
    if (iso !== lastIso.current || lastFmt.current !== fmt) {
      lastIso.current = iso;
      lastFmt.current = fmt;
      setText(isoToDisplayDate(iso, fmt));
      setInvalid(false);
    }
  }, [iso, fmt]);

  const commit = (next: string) => {
    lastIso.current = next;
    onValueChange(next);
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') el.showPicker(); else el.click();
    } catch { /* showPicker exige gesto de usuario; ignorar */ }
  };

  return (
    <>
      <Input
        {...rest}
        disabled={disabled}
        value={text}
        placeholder={rest.placeholder ?? dateFormatHint(fmt)}
        aria-invalid={invalid || undefined}
        inputMode="numeric"
        onChange={(_, d) => {
          setText(d.value);
          if (d.value.trim() === '') { setInvalid(false); commit(''); return; }
          const parsed = parseDisplayDate(d.value, fmt);
          if (parsed) { setInvalid(false); commit(parsed); }
          else if (lastIso.current !== '') commit('');
        }}
        onBlur={(e) => {
          rest.onBlur?.(e);
          if (text.trim() === '') { setInvalid(false); return; }
          const parsed = parseDisplayDate(text, fmt);
          if (parsed) { setText(isoToDisplayDate(parsed, fmt)); setInvalid(false); }
          else setInvalid(true);
        }}
        contentAfter={
          <Button
            appearance="transparent" size="small" icon={<CalendarLtr20Regular />}
            disabled={disabled} aria-label={dateFormatHint(fmt)} onClick={openPicker}
          />
        }
      />
      <input
        ref={nativeRef} type="date" tabIndex={-1} aria-hidden="true" data-testid="date-input-native"
        value={iso}
        onChange={(e) => { setText(isoToDisplayDate(e.target.value, fmt)); setInvalid(false); commit(e.target.value); }}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
      />
    </>
  );
}
